﻿import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Calculate as CalcIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Campaign as AdIcon,
  FilterList as FilterListIcon,
  Edit as EditIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  History as HistoryIcon,
  Monitor as MonitorIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Stop as StopIcon,
  Sync as SyncIcon,
  Rule as RuleIcon,
} from "@mui/icons-material";
import Cron from "react-cron-generator";
import "react-cron-generator/build/cron-builder.css";

import {
  ottoItemsApi,
  OttoMarginRulesApi,
} from "../../../../../services/api/multi/otto";
import { RepricerArticlesApi } from "../../../../../services/api/multi/repricer";
import { formatPricingHistory } from "../../../utils/pricingHistory";
import {
  ArticleSalesStatsDialog,
  SalesStatsOpenButton,
} from "../../../components/ArticleSalesStatsDialog";

// ── helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  active: "success",
  update_needed: "warning",
  error: "error",
  oos: "default",
  paused: "info",
  unknown: "default",
};

const fmt = (val, decimals = 2) =>
  val != null ? `${Number(val).toFixed(decimals)} €` : null;

const yesterdayLocalIso = () => {
  const day = new Date();
  day.setDate(day.getDate() - 1);
  return [
    day.getFullYear(),
    String(day.getMonth() + 1).padStart(2, "0"),
    String(day.getDate()).padStart(2, "0"),
  ].join("-");
};

const DetailRow = ({ label, value }) => (
  <Grid container sx={{ py: 0.4 }} alignItems="baseline">
    <Grid item xs={5}>
      <Typography variant="caption" color="text.secondary">
        {label}:
      </Typography>
    </Grid>
    <Grid item xs={7}>
      <Typography variant="caption" fontWeight={500}>
        {value ?? "—"}
      </Typography>
    </Grid>
  </Grid>
);

function ArticleExpandedDetails({ article, onNavigateToBase }) {
  const od = article.otto;
  return (
    <Box sx={{ px: 2, py: 1.5, bgcolor: "grey.50" }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Typography
            variant="subtitle2"
            color="primary"
            gutterBottom
            onClick={() => onNavigateToBase && onNavigateToBase(article.sku)}
            sx={{ cursor: "pointer", textDecoration: "underline" }}
          >
            Base
          </Typography>
          <Divider sx={{ mb: 0.5 }} />
          <DetailRow label="Name" value={article.articlename} />
          <DetailRow
            label="Plenty Article ID"
            value={article.plenty_article_id}
          />
          <DetailRow
            label="Plenty Variant ID"
            value={article.plenty_variant_id}
          />
          <DetailRow label="Parent SKU" value={article.parent_sku} />
          <DetailRow label="Child SKUs" value={article.child_skus} />

          <Divider sx={{ my: 0.5, borderStyle: "dotted" }} />

          <DetailRow label="EANs" value={article.eans?.join(", ")} />
          <DetailRow label="Weight (g)" value={article.weight} />
          <DetailRow label="Package" value={article.package_type} />
          <DetailRow
            label="Package Cost"
            value={
              article.package_cost != null
                ? `${Number(article.package_cost).toFixed(2)} €`
                : null
            }
          />

          <Divider sx={{ my: 0.5, borderStyle: "dotted" }} />

          <DetailRow label="Buy Price" value={fmt(article.brutto_buy_price)} />
          <DetailRow label="Bundle Type" value={article.bundle_type} />

          <Divider sx={{ my: 0.5, borderStyle: "dotted" }} />

          <DetailRow
            label="Last Inbound Date"
            value={article.last_inbound_date}
          />
          <DetailRow label="Article Type" value={article.article_type} />
          <DetailRow
            label="Pricing History"
            value={formatPricingHistory(article.pricing_history)}
          />
          <DetailRow
            label="Ratios (Last 10)"
            value={
              article.ratios?.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {article.ratios.slice(0, 10).map((r, i) => (
                    <Chip
                      key={i}
                      label={typeof r === "number" ? r.toFixed(2) : r}
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{ height: 18, fontSize: "0.6rem" }}
                    />
                  ))}
                </Stack>
              ) : null
            }
          />
        </Grid>
        {od && (
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Otto Pricing
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            <DetailRow label="Min Price" value={fmt(od.min_price)} />
            <DetailRow label="Max Price" value={fmt(od.max_price)} />
            <DetailRow
              label="Next Competitor"
              value={fmt(od.next_competitor_price)}
            />
            <DetailRow
              label="Provision %"
              value={
                od.provision_percentage != null
                  ? `${od.provision_percentage}%`
                  : null
              }
            />
            <DetailRow label="Category" value={od.category} />
            <DetailRow label="Moin" value={od.moin} />
          </Grid>
        )}
        {od && (
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Otto Config
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            <DetailRow
              label="Use Min/Max Rules"
              value={
                od.use_min_max_rules != null
                  ? od.use_min_max_rules
                    ? "Yes"
                    : "No"
                  : null
              }
            />
            <DetailRow label="Additional Offers" value={od.additional_offers} />
            <DetailRow
              label="Unpause Date"
              value={
                od.unpause_date
                  ? new Date(od.unpause_date).toLocaleString()
                  : null
              }
            />
            <DetailRow label="Margin Rule ID" value={od.margin_rule_id} />
            {od.product_url && (
              <DetailRow
                label="Product URL"
                value={
                  <a
                    href={od.product_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: "0.75rem" }}
                  >
                    Open
                  </a>
                }
              />
            )}
            {od.error_message && (
              <DetailRow
                label="Error"
                value={
                  <Typography variant="caption" color="error">
                    {od.error_message}
                  </Typography>
                }
              />
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function StatusChip({ status }) {
  return (
    <Chip
      label={status || "—"}
      color={STATUS_COLORS[status] || "default"}
      size="small"
      variant="outlined"
    />
  );
}

function StatsBar({ stats }) {
  if (!stats) return null;
  const items = [
    { label: "Total", value: stats.total_count, color: "primary" },
    { label: "Stock", value: stats.total_stock, color: "success" },
    ...(stats.by_status
      ? Object.entries(stats.by_status).map(([s, v]) => ({
          label: s,
          value: v.count,
          color: STATUS_COLORS[s] || "default",
        }))
      : []),
  ];
  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      sx={{ mb: 2 }}
    >
      {items.map((i) => (
        <Chip
          key={i.label}
          label={`${i.label}: ${i.value}`}
          color={i.color}
          size="small"
        />
      ))}
    </Stack>
  );
}

// ── Edit Otto Details dialog ─────────────────────────────────────────────────

function EditOttoDetailsDialog({ article, open, onClose, onSaved }) {
  const od = article?.otto || {};
  const [price, setPrice] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [moin, setMoin] = useState("");
  const [provision, setProvision] = useState("");
  const [category, setCategory] = useState("");
  const [marginRuleId, setMarginRuleId] = useState("");
  const [useMinMax, setUseMinMax] = useState(true);
  const [status, setStatus] = useState("active");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open && article) {
      setPrice(od.price ?? "");
      setMinPrice(od.min_price ?? "");
      setMaxPrice(od.max_price ?? "");
      setMoin(od.moin ?? "");
      setProvision(od.provision_percentage ?? "");
      setCategory(od.category ?? "");
      setMarginRuleId(od.margin_rule_id ?? "");
      setUseMinMax(od.use_min_max_rules ?? true);
      setStatus(od.status || "active");
      setErr(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, article]);

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      const payload = {};
      if (price !== "") payload.price = parseFloat(price);
      if (minPrice !== "") payload.min_price = parseFloat(minPrice);
      if (maxPrice !== "") payload.max_price = parseFloat(maxPrice);
      if (moin !== "") payload.moin = moin;
      if (provision !== "")
        payload.provision_percentage = parseFloat(provision);
      if (category !== "") payload.category = category;
      if (marginRuleId !== "")
        payload.margin_rule_id = parseInt(marginRuleId, 10);
      payload.use_min_max_rules = useMinMax;
      if ((status || "active") !== (od.status || "active"))
        payload.status = status || "active";
      await RepricerArticlesApi.updateOttoDetails(article.sku, payload);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Otto Details — {article?.sku}</DialogTitle>
      <DialogContent>
        {err && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {err}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Price (€)"
              size="small"
              type="number"
              fullWidth
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
            <TextField
              label="Min Price (€)"
              size="small"
              type="number"
              fullWidth
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
            />
            <TextField
              label="Max Price (€)"
              size="small"
              type="number"
              fullWidth
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Moin"
              size="small"
              fullWidth
              value={moin}
              onChange={(e) => setMoin(e.target.value)}
            />
            <TextField
              label="Provision %"
              size="small"
              type="number"
              fullWidth
              value={provision}
              onChange={(e) => setProvision(e.target.value)}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Category"
              size="small"
              fullWidth
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
            <TextField
              label="Margin Rule ID"
              size="small"
              type="number"
              fullWidth
              value={marginRuleId}
              onChange={(e) => setMarginRuleId(e.target.value)}
            />
          </Stack>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status || "active"}
              onChange={(e) => setStatus(e.target.value)}
            >
              {[
                "active",
                "paused",
                "error",
                "update_needed",
                "oos",
                "unknown",
              ].map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={useMinMax}
                onChange={(e) => setUseMinMax(e.target.checked)}
              />
            }
            label="Use Min/Max Rules"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Delete Otto Details dialog ────────────────────────────────────────────────

function DeleteOttoDetailsDialog({ sku, open, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState(null);

  const handleDelete = async () => {
    setDeleting(true);
    setErr(null);
    try {
      await RepricerArticlesApi.deleteOttoDetails(sku);
      onDeleted();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Remove Otto Details</DialogTitle>
      <DialogContent>
        {err && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {err}
          </Alert>
        )}
        <Typography>
          Remove Otto details for <strong>{sku}</strong>? The base article will
          remain.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={deleting}
        >
          {deleting ? <CircularProgress size={18} /> : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Pricing History dialog ────────────────────────────────────────────────────

function PricingHistoryDialog({ sku, open, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open && sku) {
      setLoading(true);
      setErr(null);
      RepricerArticlesApi.getPricingHistory(sku)
        .then((res) => setHistory(res.data || []))
        .catch((e) => setErr(e.response?.data?.detail || e.message))
        .finally(() => setLoading(false));
    }
  }, [open, sku]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Pricing History — {sku}</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {err && (
          <Alert severity="error" sx={{ m: 2 }}>
            {err}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.100" }}>
                  <TableCell sx={{ py: 0.5 }}>Timestamp</TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    My Price
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    Site Price
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    Sale Price
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>Buybox</TableCell>
                  <TableCell sx={{ py: 0.5 }}>Top Seller</TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    Qty
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        No history found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  history.map((h) => (
                    <TableRow key={h.id} hover>
                      <TableCell
                        sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}
                      >
                        {new Date(h.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        {h.my_price != null
                          ? `${Number(h.my_price).toFixed(2)} €`
                          : "—"}
                      </TableCell>
                      <TableCell align="right">
                        {h.site_price != null
                          ? `${Number(h.site_price).toFixed(2)} €`
                          : "—"}
                      </TableCell>
                      <TableCell align="right">
                        {h.sale_price != null
                          ? `${Number(h.sale_price).toFixed(2)} €`
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {h.is_in_buybox != null ? (
                          <Chip
                            label={h.is_in_buybox ? "Yes" : "No"}
                            color={h.is_in_buybox ? "success" : "default"}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {h.seller || "—"}
                      </TableCell>
                      <TableCell align="right">{h.quantity ?? "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Margin rules dialog ────────────────────────────────────────────────────────

function MarginRuleDialog({ article, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [rule, setRule] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !article) {
      setRule(null);
      setError(null);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    const fetchRule = async () => {
      try {
        const id = article.otto?.margin_rule_id;
        if (id != null) {
          const res = await OttoMarginRulesApi.getMarginRule(id);
          if (active) setRule(res.data);
        } else {
          if (active) setRule(null);
        }
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchRule();

    return () => {
      active = false;
    };
  }, [open, article]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Otto Margin Rule: {article?.sku}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle2" sx={{ color: "#f00020", mb: 1 }}>
              Otto ID: {article?.otto?.margin_rule_id || "None"}
            </Typography>
            {!rule ? (
              <Typography variant="caption" color="text.secondary">
                Not found or not configured
              </Typography>
            ) : (
              <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Buy Price (up to):
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {fmt(rule.buy_price)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Min Margin:
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {rule.min_margin} %
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Min Profit (Min):
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {fmt(rule.min_min_profit)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Max Margin:
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {rule.max_margin} %
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Max Profit (Min):
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {fmt(rule.max_min_profit)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Cron dialog ───────────────────────────────────────────────────────────────

function CronDialog({
  open,
  value,
  webhook,
  sync,
  onChange,
  onWebhookChange,
  onSyncChange,
  onConfirm,
  onClose,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set Otto Cron Schedule</DialogTitle>
      <DialogContent>
        <Cron value={value} showResultText onChange={onChange} />
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Additional Configuration
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FormControlLabel
            control={
              <Switch checked={sync} onChange={onSyncChange} color="primary" />
            }
            label="Sync with Otto API (Add new active articles and remove inactive ones prior to monitoring)"
            sx={{ mb: 2, display: "flex" }}
          />
          <TextField
            label="Discord Webhook URL"
            fullWidth
            size="small"
            value={webhook}
            onChange={onWebhookChange}
            placeholder="https://discord.com/api/webhooks/..."
            helperText="Send Otto monitoring results report to this Discord webhook"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onConfirm}>
          Save & Start
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function OttoArticlesSubTab({
  initialSku = null,
  onSkuConsumed,
  onNavigateToBase,
} = {}) {
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [salesStatsSku, setSalesStatsSku] = useState(null);

  // search / filter
  const [skuSearch, setSkuSearch] = useState(initialSku || "");
  const [activeSkuSearch, setActiveSkuSearch] = useState(initialSku || "");
  const [statusFilter, setStatusFilter] = useState("ALL");
  // advanced filters & sort
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [activeAdvancedFilters, setActiveAdvancedFilters] = useState([]);
  const [sortOrder, setSortOrder] = useState({ field: null, dir: null });

  const [searchTrigger, setSearchTrigger] = useState(0);

  // consume initialSku navigation
  useEffect(() => {
    if (initialSku) {
      setSkuSearch(initialSku);
      setActiveSkuSearch(initialSku);
      setPage(0);
      if (onSkuConsumed) onSkuConsumed();
    }
  }, [initialSku, onSkuConsumed]);

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);

  // expanded rows
  const [expandedRows, setExpandedRows] = useState(new Set());

  // per-row action loading
  const [recalcLoading, setRecalcLoading] = useState({});
  const [monitorLoading, setMonitorLoading] = useState({});
  const [syncRowLoading, setSyncRowLoading] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [adsSyncDialogOpen, setAdsSyncDialogOpen] = useState(false);
  const [adsFromDate, setAdsFromDate] = useState(yesterdayLocalIso);
  const [adsToDate, setAdsToDate] = useState(yesterdayLocalIso);

  // edit / delete / history dialogs
  const [editArticle, setEditArticle] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteSkuDialog, setDeleteSkuDialog] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [historySkuDialog, setHistorySkuDialog] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [marginRuleArticle, setMarginRuleArticle] = useState(null);
  const [marginRuleOpen, setMarginRuleOpen] = useState(false);

  const toggleRow = (sku) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  // cron
  const [cronStatus, setCronStatus] = useState({
    active: false,
    expression: "0 8 * * *",
  });
  const [cronDialogOpen, setCronDialogOpen] = useState(false);
  const [cronExpr, setCronExpr] = useState("0 8 * * *");
  const [cronWebhook, setCronWebhook] = useState("");
  const [cronSync, setCronSync] = useState(false);
  const [cronLoading, setCronLoading] = useState(false);

  const buildFilters = useCallback(() => {
    // always restrict to articles with otto details
    const f = [{ field: "otto.article_id", op: "is_not_null" }];
    if (activeSkuSearch)
      f.push({ field: "sku", op: "like", value: `%${activeSkuSearch}%` });
    if (statusFilter !== "ALL")
      f.push({ field: "otto.status", op: "eq", value: statusFilter });

    // advanced filters
    for (const af of activeAdvancedFilters) {
      if (af.field && af.op && af.value !== "") {
        f.push({ field: af.field, op: af.op, value: af.value });
      }
    }
    return f;
    // searchTrigger is intentionally listed to force a reload even when other deps haven't changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSkuSearch, statusFilter, searchTrigger, activeAdvancedFilters]);

  const loadArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = buildFilters();
      const orders = sortOrder.field
        ? [{ field: sortOrder.field, dir: sortOrder.dir }]
        : undefined;
      const res = await RepricerArticlesApi.getArticles({
        details: "otto",
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        filters,
        orders,
      });
      setArticles(res.data || []);
      setTotal(res.data?.length ?? 0);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [buildFilters, page, rowsPerPage, sortOrder]);

  const loadStats = useCallback(async () => {
    try {
      const res = await RepricerArticlesApi.getArticleStats("otto");
      setStats(res.data);
    } catch {
      /* silent */
    }
  }, []);

  const loadCronStatus = useCallback(async () => {
    try {
      const res = await ottoItemsApi.getCronStatus();
      setCronStatus(res.data);
      if (res.data.expression) setCronExpr(res.data.expression);
      if (res.data.arguments) {
        setCronWebhook(res.data.arguments.discord_webhook || "");
        setCronSync(res.data.arguments.sync || false);
      }
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);
  useEffect(() => {
    loadStats();
    loadCronStatus();
  }, [loadStats, loadCronStatus]);

  const handleSearch = () => {
    setPage(0);
    setActiveSkuSearch(skuSearch);
    setSearchTrigger((t) => t + 1);
    setActiveAdvancedFilters([...advancedFilters]);
  };
  const handleClearSearch = () => {
    setSkuSearch("");
    setActiveSkuSearch("");
    setPage(0);
    setSearchTrigger((t) => t + 1);
  };
  const handleSort = (field) => {
    setSortOrder((prev) => {
      const isAsc = prev.field === field && prev.dir === "asc";
      const isDesc = prev.field === field && prev.dir === "desc";
      if (isAsc) return { field, dir: "desc" };
      if (isDesc) return { field: null, dir: null };
      return { field, dir: "asc" };
    });
    setPage(0);
  };

  const handleAddAdvancedFilter = () => {
    setAdvancedFilters((prev) => [
      ...prev,
      { id: Date.now(), field: "brutto_buy_price", op: "eq", value: "" },
    ]);
  };

  const handleRemoveAdvancedFilter = (id) => {
    setAdvancedFilters((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUpdateAdvancedFilter = (id, key, val) => {
    setAdvancedFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [key]: val } : f)),
    );
  };
  const handleSyncFromOtto = async () => {
    setSyncing(true);
    setError(null);
    try {
      await ottoItemsApi.syncFromOtto();
      setSuccess("Sync from Otto triggered");
      await loadArticles();
      await loadStats();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncAdsFromOtto = async () => {
    setSyncing(true);
    setError(null);
    try {
      await ottoItemsApi.syncAdsFromOtto({
        from_date: adsFromDate,
        to_date: adsToDate,
      });
      setSuccess("Otto ads sync triggered");
      setAdsSyncDialogOpen(false);
      // Report generation can take a minute — reload shortly after trigger.
      setTimeout(() => {
        loadArticles();
        loadStats();
      }, 5000);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncArticle = async (sku) => {
    setSyncRowLoading((prev) => ({ ...prev, [sku]: true }));
    setError(null);
    try {
      await ottoItemsApi.syncFromOtto(sku);
      setSuccess(`Synced from Otto: ${sku}`);
      await loadArticles();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSyncRowLoading((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const handleRecalculateAll = async () => {
    setSyncing(true); // Reusing the syncing state for simplicity
    setError(null);
    try {
      await ottoItemsApi.recalculate();
      setSuccess("Recalculate all articles triggered");
      await loadArticles();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleMonitorAll = async () => {
    setSyncing(true); // Reusing the syncing state for simplicity
    setError(null);
    try {
      await ottoItemsApi.monitorItems();
      setSuccess("Monitor all articles triggered");
      await loadArticles();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleMonitor = async (sku) => {
    setMonitorLoading((prev) => ({ ...prev, [sku]: true }));
    setError(null);
    try {
      await ottoItemsApi.monitorItems(sku);
      setSuccess(`Monitor triggered: ${sku}`);
      await loadArticles();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setMonitorLoading((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const handleRecalculate = async (sku) => {
    setRecalcLoading((prev) => ({ ...prev, [sku]: true }));
    setError(null);
    try {
      await ottoItemsApi.recalculate(sku);
      setSuccess(`Recalculated: ${sku}`);
      await loadArticles();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setRecalcLoading((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const handleStartCron = async () => {
    setCronLoading(true);
    try {
      await ottoItemsApi.startCronJob(cronExpr, cronWebhook, cronSync);
      setSuccess(`Cron started: ${cronExpr}`);
      setCronDialogOpen(false);
      await loadCronStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCronLoading(false);
    }
  };

  const handleStopCron = async () => {
    setCronLoading(true);
    try {
      await ottoItemsApi.stopCronJob();
      setSuccess("Cron stopped");
      await loadCronStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCronLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* Stats */}
          <StatsBar stats={stats} />

          {/* Action bar */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <Button
              variant="outlined"
              startIcon={
                syncing ? <CircularProgress size={16} /> : <SyncIcon />
              }
              onClick={handleSyncFromOtto}
              disabled={syncing}
              size="small"
            >
              Sync from Otto
            </Button>

            <Button
              variant="outlined"
              color="warning"
              startIcon={syncing ? <CircularProgress size={16} /> : <AdIcon />}
              onClick={() => setAdsSyncDialogOpen(true)}
              disabled={syncing}
              size="small"
            >
              Sync Ads
            </Button>

            <Button
              variant="outlined"
              startIcon={
                syncing ? <CircularProgress size={16} /> : <CalcIcon />
              }
              onClick={handleRecalculateAll}
              disabled={syncing}
              size="small"
            >
              Recalculate All
            </Button>

            <Button
              variant="outlined"
              startIcon={
                syncing ? <CircularProgress size={16} /> : <MonitorIcon />
              }
              onClick={handleMonitorAll}
              disabled={syncing}
              size="small"
            >
              Monitor All
            </Button>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadArticles();
                loadStats();
              }}
              size="small"
            >
              Refresh
            </Button>

            <Divider orientation="vertical" flexItem />

            {/* Cron controls */}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Chip
                size="small"
                label={
                  cronStatus.active
                    ? `Cron: ON (${cronStatus.expression || ""})`
                    : "Cron: OFF"
                }
                color={cronStatus.active ? "success" : "default"}
                variant="outlined"
              />
              {cronStatus.active ? (
                <Tooltip title="Stop cron">
                  <IconButton
                    size="small"
                    onClick={handleStopCron}
                    disabled={cronLoading}
                    color="error"
                  >
                    <StopIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Configure & start cron">
                  <IconButton
                    size="small"
                    onClick={() => setCronDialogOpen(true)}
                    color="success"
                  >
                    <PlayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            {cronStatus.next_run_at && (
              <Typography variant="caption" color="text.secondary">
                Next: {new Date(cronStatus.next_run_at).toLocaleString()}
              </Typography>
            )}
          </Stack>

          {/* Filter bar */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              placeholder="Search SKU…"
              value={skuSearch}
              onChange={(e) => setSkuSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                endAdornment: skuSearch ? (
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <SearchIcon fontSize="small" />
                ),
              }}
              sx={{ width: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                {["ACTIVE", "UPDATE_NEEDED", "ERROR", "OOS", "PAUSED"].map(
                  (s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>

            <Button
              variant={advancedFiltersOpen ? "contained" : "outlined"}
              size="small"
              onClick={() => setAdvancedFiltersOpen((prev) => !prev)}
              startIcon={<FilterListIcon />}
            >
              Filters ({advancedFilters.length})
            </Button>

            {(activeSkuSearch ||
              statusFilter !== "ALL" ||
              activeAdvancedFilters.length > 0) && (
              <Button
                variant="text"
                size="small"
                onClick={handleClearSearch}
                startIcon={<ClearIcon />}
              >
                Clear
              </Button>
            )}
          </Stack>

          {/* Table */}
          <Collapse in={advancedFiltersOpen} unmountOnExit>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Typography variant="subtitle2">Advanced Filters</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddAdvancedFilter}
                >
                  Add Rule
                </Button>
              </Stack>
              {advancedFilters.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No advanced filters active.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {advancedFilters.map((f) => (
                    <Stack
                      key={f.id}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select
                          value={f.field}
                          onChange={(e) =>
                            handleUpdateAdvancedFilter(
                              f.id,
                              "field",
                              e.target.value,
                            )
                          }
                        >
                          <MenuItem value="brutto_buy_price">
                            Buy Price
                          </MenuItem>
                          <MenuItem value="stock">Stock</MenuItem>
                          <MenuItem value="package_type">Package Type</MenuItem>
                          <MenuItem value="weight">Weight</MenuItem>
                          <MenuItem value="otto.price">Otto Price</MenuItem>
                          <MenuItem value="otto.site_price">
                            Site Price
                          </MenuItem>
                          <MenuItem value="otto.sale_price">
                            Sale Price
                          </MenuItem>
                          <MenuItem value="otto.expected_profit">
                            Expected Profit
                          </MenuItem>
                          <MenuItem value="otto.moin">Moin</MenuItem>
                          <MenuItem value="otto.seller">Seller</MenuItem>
                          <MenuItem value="otto.use_min_max_rules">
                            Use Min/Max Rules
                          </MenuItem>
                          <MenuItem value="otto.is_in_buybox">
                            Is in Buybox
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                          value={f.op}
                          onChange={(e) =>
                            handleUpdateAdvancedFilter(
                              f.id,
                              "op",
                              e.target.value,
                            )
                          }
                        >
                          <MenuItem value="eq">Equals (=)</MenuItem>
                          <MenuItem value="gt">Greater (&gt;)</MenuItem>
                          <MenuItem value="gte">Greater eq (&gt;=)</MenuItem>
                          <MenuItem value="lt">Less (&lt;)</MenuItem>
                          <MenuItem value="lte">Less eq (&lt;=)</MenuItem>
                          <MenuItem value="like">Contains</MenuItem>
                          <MenuItem value="is_null">Is null/none</MenuItem>
                          <MenuItem value="is_not_null">Is not null</MenuItem>
                        </Select>
                      </FormControl>
                      {f.op !== "is_null" && f.op !== "is_not_null" && (
                        <TextField
                          size="small"
                          placeholder="Value"
                          value={f.value}
                          onChange={(e) =>
                            handleUpdateAdvancedFilter(
                              f.id,
                              "value",
                              e.target.value,
                            )
                          }
                          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                          sx={{ width: 140 }}
                        />
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveAdvancedFilter(f.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSearch}
                      sx={{ mt: 1 }}
                    >
                      Apply Filters
                    </Button>
                  </Box>
                </Stack>
              )}
            </Paper>
          </Collapse>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell padding="checkbox" />
                  <TableCell>
                    <TableSortLabel
                      active={sortOrder.field === "sku"}
                      direction={
                        sortOrder.field === "sku" ? sortOrder.dir : "asc"
                      }
                      onClick={() => handleSort("sku")}
                    >
                      SKU
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortOrder.field === "otto.price"}
                      direction={
                        sortOrder.field === "otto.price" ? sortOrder.dir : "asc"
                      }
                      onClick={() => handleSort("otto.price")}
                    >
                      My Price
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortOrder.field === "otto.site_price"}
                      direction={
                        sortOrder.field === "otto.site_price"
                          ? sortOrder.dir
                          : "asc"
                      }
                      onClick={() => handleSort("otto.site_price")}
                    >
                      Site Price
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Sale Price</TableCell>
                  <TableCell align="right">Exp. Profit</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortOrder.field === "otto.status"}
                      direction={
                        sortOrder.field === "otto.status"
                          ? sortOrder.dir
                          : "asc"
                      }
                      onClick={() => handleSort("otto.status")}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortOrder.field === "otto.is_in_buybox"}
                      direction={
                        sortOrder.field === "otto.is_in_buybox"
                          ? sortOrder.dir
                          : "asc"
                      }
                      onClick={() => handleSort("otto.is_in_buybox")}
                    >
                      Buybox
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Seller</TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortOrder.field === "stock"}
                      direction={
                        sortOrder.field === "stock" ? sortOrder.dir : "asc"
                      }
                      onClick={() => handleSort("stock")}
                    >
                      Stock
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Child SKUs</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : articles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No articles found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  articles.map((a) => {
                    const od = a.otto;
                    const expanded = expandedRows.has(a.sku);
                    return (
                      <React.Fragment key={a.sku}>
                        <TableRow
                          hover
                          sx={{
                            cursor: "pointer",
                            "& > td": expanded ? { borderBottom: "none" } : {},
                          }}
                          onClick={() => toggleRow(a.sku)}
                        >
                          <TableCell padding="checkbox">
                            <IconButton size="small">
                              {expanded ? (
                                <CollapseIcon fontSize="small" />
                              ) : (
                                <ExpandIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                              component="span"
                            >
                              {od?.product_url ? (
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  component="a"
                                  href={od.product_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                    textDecoration: "underline",
                                    color: "primary.main",
                                  }}
                                >
                                  {a.sku}
                                </Typography>
                              ) : (
                                <Typography variant="body2" fontWeight={500}>
                                  {a.sku}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {fmt(od?.price) ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {fmt(od?.site_price) ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: "red" }}>
                              {fmt(od?.sale_price) ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {fmt(od?.expected_profit) ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={od?.status} />
                          </TableCell>
                          <TableCell>
                            {od?.is_in_buybox != null ? (
                              <Chip
                                label={od.is_in_buybox ? "Yes" : "No"}
                                color={od.is_in_buybox ? "success" : "default"}
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: 120 }}
                            >
                              {od?.seller || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {a.stock ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}
                            >
                              {od?.updated_at
                                ? new Date(od.updated_at).toLocaleString()
                                : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.72rem", maxWidth: 120 }}
                              noWrap
                            >
                              {a.child_skus || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <SalesStatsOpenButton
                                onClick={() => setSalesStatsSku(a.sku)}
                              />
                              <Tooltip title="Edit Otto details">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditArticle(a);
                                      setEditOpen(true);
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Sync article">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => handleSyncArticle(a.sku)}
                                    disabled={!!syncRowLoading[a.sku]}
                                  >
                                    {syncRowLoading[a.sku] ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <SyncIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Monitor (trigger price check)">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleMonitor(a.sku)}
                                    disabled={!!monitorLoading[a.sku]}
                                  >
                                    {monitorLoading[a.sku] ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <MonitorIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Recalculate">
                                <span>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "purple" }}
                                    onClick={() => handleRecalculate(a.sku)}
                                    disabled={!!recalcLoading[a.sku]}
                                  >
                                    {recalcLoading[a.sku] ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <CalcIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="View Margin Rules">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => {
                                    setMarginRuleArticle(a);
                                    setMarginRuleOpen(true);
                                  }}
                                >
                                  <RuleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Pricing history">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setHistorySkuDialog(a.sku);
                                      setHistoryOpen(true);
                                    }}
                                  >
                                    <HistoryIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Remove Otto details">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setDeleteSkuDialog(a.sku);
                                      setDeleteOpen(true);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>

                        {/* Expanded details row */}
                        <TableRow>
                          <TableCell
                            colSpan={13}
                            sx={{ p: 0, border: expanded ? undefined : "none" }}
                          >
                            <Collapse
                              in={expanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <ArticleExpandedDetails
                                article={a}
                                onNavigateToBase={onNavigateToBase}
                              />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={stats?.total_count ?? total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(+e.target.value);
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </CardContent>
      </Card>

      <CronDialog
        open={cronDialogOpen}
        value={cronExpr}
        webhook={cronWebhook}
        sync={cronSync}
        onChange={(v) => setCronExpr(v)}
        onWebhookChange={(e) => setCronWebhook(e.target.value)}
        onSyncChange={(e) => setCronSync(e.target.checked)}
        onConfirm={handleStartCron}
        onClose={() => setCronDialogOpen(false)}
      />

      <Dialog
        open={adsSyncDialogOpen}
        onClose={() => !syncing && setAdsSyncDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Sync Otto Ads</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="From date"
              type="date"
              value={adsFromDate}
              onChange={(event) => setAdsFromDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="To date"
              type="date"
              value={adsToDate}
              onChange={(event) => setAdsToDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Typography variant="body2" color="text.secondary">
              Otto reports are split into inclusive batches of up to 90 days.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAdsSyncDialogOpen(false)}
            disabled={syncing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSyncAdsFromOtto}
            disabled={syncing || !adsFromDate || !adsToDate}
            variant="contained"
          >
            Sync Ads
          </Button>
        </DialogActions>
      </Dialog>

      {editArticle && (
        <EditOttoDetailsDialog
          article={editArticle}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            setSuccess(`Saved Otto details for ${editArticle.sku}`);
            loadArticles();
          }}
        />
      )}

      <DeleteOttoDetailsDialog
        sku={deleteSkuDialog}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          setDeleteOpen(false);
          setSuccess(`Removed Otto details for ${deleteSkuDialog}`);
          loadArticles();
          loadStats();
        }}
      />

      <PricingHistoryDialog
        sku={historySkuDialog}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />

      <MarginRuleDialog
        article={marginRuleArticle}
        open={marginRuleOpen}
        onClose={() => setMarginRuleOpen(false)}
      />

      <ArticleSalesStatsDialog
        open={!!salesStatsSku}
        onClose={() => setSalesStatsSku(null)}
        sku={salesStatsSku}
        articlename={articles.find((x) => x.sku === salesStatsSku)?.articlename}
        channel="otto"
      />
    </Box>
  );
}
